// 第三方组件二开，AI 生成注释，自行辨别参考
import { focusableSelector } from "./constants";
import { getTargetPosition } from "./positioning";

export interface Coords {
  x: number;
  y: number;
}

export interface Dims {
  width: number;
  height: number;
}

export interface ElementInfo {
  dims: Dims;
  coords: Coords;
}

/**
 * 判断给定的坐标是否有效
 *
 * @param coords 坐标对象，包含x和y属性
 * @returns 返回布尔值，表示坐标是否有效
 */
export function isValidCoords(coords: Coords | undefined): boolean {
  if (!coords) {
    return false;
  } else if ((!coords.x && coords.x !== 0) || (!coords.y && coords.y !== 0)) {
    return false;
  } else {
    return true;
  }
}

/**
 * 判断给定的尺寸是否有效
 *
 * @param dims 尺寸对象，包含高度和宽度
 * @returns 返回布尔值，表示尺寸是否有效
 */
export function isValidDims(dims: Dims): boolean {
  if (!dims) {
    return false;
  } else if ((!dims.height && dims.height !== 0) || (!dims.width && dims.height !== 0)) {
    return false;
  } else if (dims.height < 0 || dims.width < 0) {
    return false;
  } else {
    return true;
  }
}

/**
 * 计算两个坐标点之间的欧几里得距离
 *
 * @param a 坐标点a，可选
 * @param b 坐标点b，可选
 * @returns 返回距离，如果参数无效则返回undefined
 */
export function dist(a: Coords | undefined, b: Coords | undefined): number | undefined {
  if (!a || !b || !isValidCoords(a) || !isValidCoords(b)) {
    return;
  }

  return Math.sqrt(
    Math.pow((Math.abs(a.x - b.x)), 2) +
    Math.pow((Math.abs(a.y - b.y)), 2))
}

/**
 * 计算两个矩形区域的面积差
 *
 * @param a 第一个矩形区域的尺寸对象
 * @param b 第二个矩形区域的尺寸对象
 * @returns 返回两个矩形区域的面积差，如果传入的尺寸对象不合法则返回undefined
 */
export function areaDiff(a: Dims, b: Dims): number | undefined {
  if (!isValidDims(a) || !isValidDims(b)) {
    return;
  }

  return Math.abs((a.height * a.width) - (b.height * b.width));
}

/**
 * 获取元素的坐标
 *
 * @param element 元素对象
 * @returns 返回元素左上角在视窗中的 x、y 坐标，若元素不存在则返回 undefined
 */
export function getElementCoords(element: Element): Coords | undefined {
  if (!element) {
    return;
  }
  const elementData: ClientRect = element.getBoundingClientRect();
  return {
    x: elementData.left,
    y: elementData.top
  }
}

/**
 * 获取元素的尺寸
 *
 * @param element 元素
 * @returns 元素尺寸，如果元素不存在则返回undefined
 */
export function getElementDims(element?: Element): Dims | undefined {
  if (!element) {
    return;
  }
  const elementData = element.getBoundingClientRect();
  return {
    width: elementData.width,
    height: elementData.height
  }
}

//https://github.com/GreenGremlin/scroll-doc/blob/master/index.js
/**
 * 获取默认滚动元素
 *
 * @returns 返回默认的滚动元素
 */
export function getDefaultScrollingElement(): Element {
  const windowStart: number = window.pageYOffset; //slightly better support than scrollY
  document.documentElement.scrollTop = windowStart + 1;
  if (window.pageXOffset > windowStart) {
    document.documentElement.scrollTop = windowStart; //reset
    return document.documentElement;
  } else {
    return document.scrollingElement || document.body;
  }
}

/**
 * 判断给定的根元素是否为默认的滚动元素
 *
 * @param root 根元素
 * @returns 如果根元素与 document.body、document.scrollingElement 或 document.documentElement 相同，则返回 true，否则返回 false
 */
export function isDefaultScrollingElement(root: Element) {
  return root.isSameNode(document.body) || root.isSameNode(document.scrollingElement) || root.isSameNode(document.documentElement);
}

//https://gist.github.com/gre/296291b8ce0d8fe6e1c3ea4f1d1c5c3b
/**
 * 获取最近的滚动祖先元素
 *
 * @param element 元素
 * @returns 最近的滚动祖先元素
 */
export function getNearestScrollAncestor(element: Element): Element {
  const regex = /(auto|scroll)/;

  const style = (el: Element, prop: string) =>
    getComputedStyle(el, null).getPropertyValue(prop);

  const scroll = (el: Element) =>
    regex.test(
      style(el, "overflow") +
      style(el, "overflow-y") +
      style(el, "overflow-x"));

  if (!element || isDefaultScrollingElement(element)) {
    return getDefaultScrollingElement();
  } else {
    if (scroll(element)) {
      return element;
    } else {
      return getNearestScrollAncestor(element.parentElement as HTMLElement)
    }
  }
}

//if we're not putting the portal in a custom container, it needs to be at the body level
/**
 * 获取有效的 Portal 根元素
 *
 * @param root 根元素
 * @returns 返回有效的 Portal 根元素
 */
export function getValidPortalRoot(root: Element) {
  // check for the potential default scrolling elements that might be returned from the above function
  if (isDefaultScrollingElement(root)) {
    return document.body;
  } else {
    return root;
  }
}

/**
 * 获取组合后的数据，包括坐标和尺寸
 *
 * @param aCoords 组件a的坐标
 * @param aDims 组件a的尺寸
 * @param bCoords 组件b的坐标
 * @param bDims 组件b的尺寸
 * @returns 返回组合后的数据，包括坐标和尺寸
 */
export function getCombinedData(aCoords: Coords, aDims: Dims, bCoords: Coords, bDims: Dims): { coords: Coords, dims: Dims } {

  // 生成与 getBoundingClientRect 类似的数据，但使用假设的位置
  // generates similar data as getBoundingClientRect but using hypothetical positions
  const generateBounds = (coords: Coords, dims: Dims): { left: number, right: number, top: number, bottom: number } => {
    return {
      // 左边界
      left: coords.x,
      // 右边界
      right: coords.x + dims.width,
      // 上边界
      top: coords.y,
      // 下边界
      bottom: coords.y + dims.height
    }
  }

  // 获取两个数中的最大（或最小）值
  const mostExtreme = (a: number, b: number, largest: boolean): number => {
    return (a > b)
      ? (largest ? a : b)
      : (largest ? b : a)
  }

  // 生成 a的边界
  const aBounds = generateBounds(aCoords, aDims);
  // 生成b的边界
  const bBounds = generateBounds(bCoords, bDims);


  // 获取左右边界的最小值
  const left: number = mostExtreme(aBounds.left, bBounds.left, false);
  // 获取右边界的最大值
  const right: number = mostExtreme(aBounds.right, bBounds.right, true);
  // 获取上边界的最小值
  const top: number = mostExtreme(aBounds.top, bBounds.top, false);
  // 获取下边界的最大值
  const bottom: number = mostExtreme(aBounds.bottom, bBounds.bottom, true);

  return {
    coords: {
      // 返回的坐标
      x: left,
      y: top
    },
    dims: {
      // 返回的高度
      height: bottom - top,
      // 返回的宽度
      width: right - left
    }
  }
}

// determines if a can fit within b
/**
 * 判断aDims是否能在bDims内放置
 *
 * @param aDims aDims的尺寸
 * @param bDims bDims的尺寸
 * @returns 返回布尔值，表示aDims是否能在bDims内放置
 */
export function fitsWithin(aDims: Dims, bDims: Dims) {
  if (!isValidDims(aDims) || !isValidDims(bDims)) {
    return false;
  }

  return aDims.height <= bDims.height && aDims.width <= bDims.width;
}

// determines if a does fit within b at the given coords
/**
 * 判断矩形aDims是否在矩形bDims内部
 *
 * @param aDims 矩形aDims的尺寸
 * @param bDims 矩形bDims的尺寸
 * @param aCoords 矩形aDims的位置坐标，可选
 * @param bCoords 矩形bDims的位置坐标，可选
 * @returns 如果矩形aDims在矩形bDims内部，则返回true，否则返回false
 */
export function isWithinAt(aDims: Dims, bDims: Dims, aCoords?: Coords, bCoords?: Coords) {
  if (!isValidDims(aDims) || !isValidDims(bDims)) {
    return false;
  }

  const validCoordsA: Coords | undefined = isValidCoords(aCoords) ? aCoords : { x: 0, y: 0 };
  const validCoordsB: Coords | undefined = isValidCoords(bCoords) ? bCoords : { x: 0, y: 0 };
  const fitsDims: boolean = fitsWithin(aDims, bDims);
  const fitsHorizontally: boolean = !!validCoordsA && !!validCoordsB && (validCoordsA.x >= validCoordsB.x) && (validCoordsA.x + aDims.width <= validCoordsB.x + bDims.width);
  const fitsVertically: boolean = !!validCoordsA && !!validCoordsB && (validCoordsA.y >= validCoordsB.y) && (validCoordsA.y + aDims.height <= validCoordsB.y + bDims.height);

  return fitsDims && fitsHorizontally && fitsVertically;
}

/**
 * 获取可聚焦元素
 *
 * @param root 根元素
 * @param includeSelf 是否包含根元素本身
 * @returns 返回可聚焦元素的数组
 */
export function getFocusableElements(root: Element, includeSelf?: boolean): HTMLElement[] {

  const focusableChildren = root.querySelectorAll(focusableSelector)
  let array: HTMLElement[] = [];
  if (includeSelf && root.matches(focusableSelector)) {
    array.push(root as HTMLElement);
  }
  if (focusableChildren.length > 0) {
    focusableChildren.forEach(el => array.push(el as HTMLElement));
  } 

  return array;
}

// helper function to get first/last focusable elements if possible
/**
 * 获取边缘可聚焦元素
 *
 * @param defaultElement 默认元素
 * @param container 容器元素，可选
 * @param includeSelf 是否包含自身元素，可选
 * @returns 返回一个包含起始元素和结束元素的对象
 */
export function getEdgeFocusables(defaultElement: HTMLElement, container?: HTMLElement, includeSelf?: boolean): { start: HTMLElement, end: HTMLElement } {
  if (container) {
    const containerFocusables: HTMLElement[] = getFocusableElements(container, includeSelf);
    if (containerFocusables.length > 0) {
      return {
        start: containerFocusables[0],
        end: containerFocusables[containerFocusables.length - 1]
      }
    }
  }

  return {
    start: defaultElement,
    end: defaultElement
  }
}

/**
 * 获取目标元素信息
 *
 * @param root 根元素
 * @param target 目标元素，可选
 * @returns 返回目标元素信息，如果不存在则返回 undefined
 */
export function getTargetInfo(root: Element, target?: HTMLElement): ElementInfo | undefined {
  if (!root || !target) {
    return;
  }
  const dims: Dims = getElementDims(target) as Dims;
  const coords: Coords = getTargetPosition(root, target);

  return {
    coords,
    dims
  }
}

/**
 * 判断指定元素下是否存在符合选择器的元素
 *
 * @param root 根元素
 * @param selector 选择器
 * @returns 如果不存在则返回true，否则返回false
 */
export function isForeignTarget(root: Element, selector: string): boolean {
  return !root.querySelector(selector);
}
